const STOP_ACTIVITY_TYPES = new Set(["short_stop", "stopped", "parked"]);
const MOVEMENT_ACTIVITY_TYPES = new Set(["movement_confirmed", "movement_resumed", "moving"]);

function validTime(value) {
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.getTime() : null;
}

export function boundaryDisplay(boundaryConfidence, status) {
  if (boundaryConfidence === "confirmed_stop") return "Ended at confirmed stop";
  if (boundaryConfidence === "uncertain_gap") return "Boundary uncertain due to GPS gap";
  if (boundaryConfidence === "open_range" || status === "ongoing") return "Ongoing in selected range";
  return "Trip boundary unavailable";
}

export function tripStatusDisplay(status) {
  return status === "ongoing" ? "Ongoing" : "Completed";
}

export function coverageExplanation(trip) {
  if (Number(trip?.movementGapCount) > 0) {
    return "Distance and route may be incomplete because some GPS positions are missing.";
  }
  if (["limited", "poor"].includes(trip?.coverage?.classification)) {
    return "Some portions of this trip were not observed continuously by GPS.";
  }
  return "GPS observations were continuous enough for this trip summary.";
}

function stopActivity(stop, boundary = false) {
  return Object.freeze({
    type: stop.classification,
    at: stop.startAt,
    label: stop.classification === "short_stop"
      ? "Short stop"
      : stop.classification === "parked" ? "Parked" : "Stopped",
    stop: Object.freeze({ ...stop, boundary }),
    evidenceTypes: Object.freeze([stop.classification]),
  });
}

export function buildOperatorTimeline(trip) {
  const evidence = Array.isArray(trip?.activities) ? trip.activities : [];
  const events = evidence
    .filter((activity) => !STOP_ACTIVITY_TYPES.has(activity?.type) && validTime(activity?.at) !== null)
    .map((activity) => Object.freeze({
      ...activity,
      evidenceTypes: Object.freeze([activity.type]),
    }));
  for (const stop of Array.isArray(trip?.stops) ? trip.stops : []) {
    if (validTime(stop?.startAt) !== null && validTime(stop?.endAt) !== null) events.push(stopActivity(stop));
  }
  if (trip?.boundaryStop && validTime(trip.boundaryStop.startAt) !== null &&
      validTime(trip.boundaryStop.endAt) !== null) {
    events.push(stopActivity(trip.boundaryStop, true));
  }
  events.sort((left, right) => validTime(left.at) - validTime(right.at) || left.type.localeCompare(right.type));

  const displayed = [];
  let gapPending = false;
  for (const event of events) {
    if (event.type === "gps_gap") {
      gapPending = true;
      const previous = displayed.at(-1);
      if (previous?.type === "gps_gap" && validTime(previous.at) === validTime(event.at)) {
        displayed[displayed.length - 1] = Object.freeze({
          ...previous,
          evidenceTypes: Object.freeze([...previous.evidenceTypes, event.type]),
        });
      } else {
        displayed.push(Object.freeze({ ...event, label: "GPS coverage gap" }));
      }
      continue;
    }
    if (gapPending && MOVEMENT_ACTIVITY_TYPES.has(event.type)) {
      const previous = displayed.at(-1);
      const sameTimestampGap = previous?.type === "gps_gap" && validTime(previous.at) === validTime(event.at);
      if (sameTimestampGap) displayed.pop();
      displayed.push(Object.freeze({
        ...event,
        type: "gps_coverage_restored",
        label: "GPS coverage restored",
        detail: "Movement observed after GPS gap; the exact path through the gap remains unknown.",
        evidenceTypes: Object.freeze([
          ...(sameTimestampGap ? previous.evidenceTypes : []), ...event.evidenceTypes,
        ]),
      }));
      gapPending = false;
      continue;
    }
    displayed.push(event);
  }
  return Object.freeze(displayed);
}
